import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export data as Excel (.xlsx)
 * @param {Array} data - Array of objects
 * @param {string} filename - File name without extension
 * @param {string} sheetName - Sheet name
 */
export const exportToExcel = (data, filename = 'export', sheetName = 'Sheet1') => {
  if (!data || data.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
};

/**
 * Export data as CSV
 * @param {Array} data - Array of objects
 * @param {string} filename - File name without extension
 */
export const exportToCSV = (data, filename = 'export') => {
  if (!data || data.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(data);
  const csv = XLSX.utils.sheet_to_csv(ws);
  saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`);
};

/**
 * Export table data as PDF
 * @param {Array} columns - [{title, dataKey}]
 * @param {Array} rows - Array of objects
 * @param {string} title - Report title
 * @param {string} filename - File name without extension
 */
export const exportToPDF = (columns, rows, title = 'Report', filename = 'report') => {
  if (!rows || rows.length === 0) return;
  const doc = new jsPDF('l', 'mm', 'a4');

  // Title
  doc.setFontSize(16);
  doc.setTextColor(30, 30, 46);
  doc.text(title, 14, 15);

  // Date
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 140);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, 22);

  // Table
  autoTable(doc, {
    startY: 28,
    head: [columns.map(c => c.title)],
    body: rows.map(row => columns.map(c => {
      const val = c.dataKey.split('.').reduce((obj, key) => obj?.[key], row);
      return val ?? '-';
    })),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 252] },
    margin: { left: 14, right: 14 },
  });

  doc.save(`${filename}.pdf`);
};

/**
 * Format data for export (flatten nested objects)
 * @param {Array} data - Raw data array
 * @param {Object} fieldMap - { displayName: 'path.to.field' }
 * @returns {Array} Flattened data
 */
export const flattenForExport = (data, fieldMap) => {
  return data.map(row => {
    const flat = {};
    Object.entries(fieldMap).forEach(([label, path]) => {
      const val = path.split('.').reduce((obj, key) => obj?.[key], row);
      flat[label] = val ?? '';
    });
    return flat;
  });
};
